import React, { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';

export type PieDatum = { name: string; value: number; color?: string };

interface Props {
  title?: string;
  data: PieDatum[];
  height?: number;
  showLegend?: boolean;
  onSliceClick?: (name: string, meta?: { shiftKey?: boolean }) => void;
}

const EChartsPie: React.FC<Props> = ({ title, data, height = 280, showLegend = true, onSliceClick }) => {
  const option: EChartsOption = useMemo(() => {
    const seriesData = data.map((d) => ({
      name: d.name,
      value: d.value,
      itemStyle: d.color ? { color: d.color } : undefined,
    }));

    const isDark = document.documentElement.classList?.contains('mantine-dark')
      || window.matchMedia?.('(prefers-color-scheme: dark)')?.matches;

    const opts: EChartsOption = {
      title: title ? {
        text: title,
        left: 'center',
        textStyle: {
          color: isDark ? '#e5e7eb' : '#000',
        },
      } : undefined,
      tooltip: {
        trigger: 'item',
        formatter: '{b}: <b>{c}</b> ({d}%)',
        backgroundColor: isDark ? 'rgba(17,24,39,0.9)' : 'rgba(255,255,255,0.9)',
        textStyle: {
          color: isDark ? '#f9fafb' : '#000',
        },
      },
      legend: showLegend ? {
        orient: 'horizontal',
        bottom: 0,
        textStyle: {
          color: isDark ? '#e5e7eb' : '#000',
        },
      } : undefined,
      series: [
        {
          type: 'pie',
          radius: '60%',
          data: seriesData,
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: 'rgba(0, 0, 0, 0.5)',
            },
          },
          label: {
            formatter: (params: unknown) => {
              const p = params as { value?: number; percent?: number };
              const value = p.value || 0;
              const percent = p.percent || 0;
              if (!value || value === 0) return '';
              if (percent < 6) return '';
              return String(value);
            },
            color: isDark ? '#e5e7eb' : '#000',
          },
        },
      ],
    };

    return opts;
  }, [title, data, height, showLegend]);

  const onEvents = useMemo(() => {
    if (!onSliceClick) return undefined;
    
    return {
      click: (params: { name: string; event?: { event?: { shiftKey?: boolean } } }) => {
        const name = params.name;
        const shiftKey = params.event?.event?.shiftKey || false;
        onSliceClick(name, { shiftKey });
      },
    };
  }, [onSliceClick]);

  return (
    <ReactECharts
      option={option}
      style={{ height: `${height}px`, width: '100%' }}
      onEvents={onEvents}
      opts={{ renderer: 'canvas' }}
    />
  );
};

export default EChartsPie;
